import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  loadStudio, writeDraftNow, updateDraft, scheduleDraft, queueAction, toggleAccount, syncAccounts, publishNow, saveTemplate, resumeJobs,
} from "@/lib/social.functions";
import { renderGraphic, PALETTES, SIZES, STYLES, CUTOUTS, type SizeKey, type Style } from "@/lib/socialRender";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CarouselMaker, ShortVideoMaker } from "@/components/admin/StudioMakers";
import { HourlyQueue, MediaQueue } from "@/components/admin/QueuePanels";

export const Route = createFileRoute("/_authenticated/admin/social")({
  head: () => ({ meta: [
    { title: "Social Studio · Colorado Web Impressions" },
    { name: "description", content: "Manage Colorado business marketing posts, graphics, and GoHighLevel publishing." },
    { property: "og:title", content: "Social Studio · Colorado Web Impressions" },
    { property: "og:description", content: "Manage Colorado business marketing posts and scheduled publishing." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
    { name: "robots", content: "noindex,nofollow" },
  ] }),
  validateSearch: (q: Record<string, unknown>): { draft?: string } => (typeof q.draft === "string" ? { draft: q.draft } : {}),
  component: SocialStudio,
});

type Studio = Awaited<ReturnType<typeof loadStudio>>;
const FORMATS = [["tip_card", "Tip card"], ["quick_win", "Quick win"], ["client_myth", "Client myth"], ["motivation", "Motivation"]] as const;
const dayKey = (d: Date) => d.toISOString().slice(0, 10);

function SocialStudio() {
  const load = useServerFn(loadStudio);
  const write = useServerFn(writeDraftNow);
  const upd = useServerFn(updateDraft);
  const sched = useServerFn(scheduleDraft);
  const qa = useServerFn(queueAction);
  const tog = useServerFn(toggleAccount);
  const sync = useServerFn(syncAccounts);
  const pub = useServerFn(publishNow);
  const saveTpl = useServerFn(saveTemplate);
  const resume = useServerFn(resumeJobs);

  const [s, setS] = useState<Studio | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { draft: wantDraft } = Route.useSearch();
  const [selId, setSelId] = useState<string | null>(wantDraft ?? null);
  const [busy, setBusy] = useState<string | null>(null);
  const [newFormat, setNewFormat] = useState<string>("tip_card");
  const [newTopic, setNewTopic] = useState("");

  const refresh = useCallback(async () => {
    try {
      const studio = await load();
      setS(studio);
      setLoadError(null);
      setSelId((id) => id ?? studio.drafts[0]?.id ?? null);
    } catch (e) {
      setLoadError((e as Error).message || "Unable to load your saved posts.");
    }
  }, [load]);
  useEffect(() => { refresh(); }, [refresh]);

  const run = async (k: string, fn: () => Promise<unknown>, ok?: string) => {
    setBusy(k);
    try { await fn(); if (ok) toast.success(ok); await refresh(); } catch (e) { toast.error((e as Error).message); } finally { setBusy(null); }
  };

  const sel = s?.drafts.find((d: any) => d.id === selId) ?? null;
  const paused = s?.jobs.filter((j: any) => j.paused_reason) ?? [];

  if (!s) return (
    <div className="mx-auto max-w-7xl p-6 space-y-4 text-foreground">
      <h1 className="text-3xl font-bold">Social Studio</h1>
      {loadError ? <div role="alert" className="space-y-3">
        <p className="text-destructive">{loadError}</p>
        <Button variant="outline" onClick={refresh}>Retry loading posts</Button>
      </div> : <p role="status" className="text-muted-foreground">Loading your Colorado marketing posts…</p>}
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-6 text-foreground">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Social Studio</h1>
          <a href="/admin/library" className="text-sm text-primary underline">Content Library →</a>
          <p className="text-muted-foreground">Marketing tips for Colorado businesses</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={newFormat} onValueChange={setNewFormat}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>{FORMATS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Input className="w-64" placeholder="Topic (optional)" value={newTopic} onChange={(e) => setNewTopic(e.target.value)} />
          <Button disabled={!!busy} onClick={() => run("write", async () => {
            const r = await write({ data: { format: newFormat as any, topic: newTopic || undefined } }); setSelId(r.id);
          }, "Draft written")}>{busy === "write" ? "Writing…" : "Write draft now"}</Button>
          <Button variant="outline" disabled={!!busy} onClick={() => run("pub", async () => {
            const r = await pub(); toast.message(`Published ${r.published}, failed ${r.failed}`);
          })}>Publish due now</Button>
        </div>
      </div>

      {loadError && <div role="alert" className="flex items-center gap-3 text-destructive"><p>{loadError}</p><Button variant="outline" onClick={refresh}>Retry</Button></div>}
      {paused.length > 0 && (
        <Card className="p-4 border-destructive flex items-center justify-between gap-3">
          <p className="text-sm">Automation paused: {paused.map((j: any) => `${j.id} — ${j.paused_reason}`).join("; ")}</p>
          <Button size="sm" onClick={() => run("resume", () => resume(), "Resumed")}>Resume</Button>
        </Card>
      )}

      {s && <Calendar posts={s.posts} drafts={s.drafts} onPick={setSelId} />}

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <Card className="p-4 space-y-2 max-h-[720px] overflow-auto">
          <h2 className="font-semibold">Saved posts ({s.drafts.length})</h2>
          {s?.drafts.map((d: any) => (
            <Button variant="outline" key={d.id} onClick={() => setSelId(d.id)}
              className={`h-auto w-full flex-col items-start whitespace-normal text-left rounded-md border p-3 text-sm ${selId === d.id ? "border-primary bg-accent" : "border-border"}`}>
              <div className="flex gap-2 mb-1"><Badge variant="secondary">{d.format}</Badge><Badge variant={d.status === "approved" ? "default" : "outline"}>{d.status}</Badge></div>
              <div className="font-medium line-clamp-2">{d.headline}</div>
            </Button>
          ))}
        </Card>
        {sel ? (
          <DraftEditor key={sel.id} draft={sel} accounts={s.accounts} busy={busy}
            onSave={(patch) => run("save", () => upd({ data: { id: sel.id, ...patch } }), "Saved")}
            onReject={() => run("rej", async () => { await upd({ data: { id: sel.id, status: "rejected" } }); setSelId(null); }, "Rejected")}
            onSchedule={(when, ids) => run("sched", () => sched({ data: { draftId: sel.id, scheduledFor: when, accountIds: ids } }), "Scheduled")}
          />
        ) : <Card className="p-8 text-muted-foreground">Select a draft to edit, render graphics, and schedule.</Card>}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-4 space-y-3">
          <h2 className="font-semibold">Queue</h2>
          {s?.posts.length === 0 && <p className="text-sm text-muted-foreground">Nothing scheduled.</p>}
          {s?.posts.map((p: any) => {
            const d = s.drafts.find((x: any) => x.id === p.draft_id);
            return (
              <div key={p.id} className="border border-border rounded-md p-3 text-sm space-y-1">
                <div className="flex justify-between gap-2"><span className="font-medium line-clamp-1">{d?.headline ?? "Draft"}</span><Badge variant={p.status === "failed" ? "destructive" : "secondary"}>{p.status}</Badge></div>
                <div className="text-muted-foreground">{new Date(p.scheduled_for).toLocaleString()} · attempts {p.attempts}</div>
                {p.last_error && <div className="text-destructive text-xs break-all">{p.last_error}</div>}
                <div className="flex gap-2">
                  {["failed", "cancelled", "retrying"].includes(p.status) && <Button size="sm" variant="outline" onClick={() => run("q", () => qa({ data: { id: p.id, action: "retry" } }))}>Retry</Button>}
                  {["queued", "retrying"].includes(p.status) && <Button size="sm" variant="outline" onClick={() => run("q", () => qa({ data: { id: p.id, action: "cancel" } }))}>Cancel</Button>}
                  <Button size="sm" variant="ghost" onClick={() => run("q", () => qa({ data: { id: p.id, action: "delete" } }))}>Delete</Button>
                </div>
              </div>
            );
          })}
        </Card>

        <div className="space-y-6">
          <Card className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">Connected accounts</h2>
              <Button size="sm" variant="outline" disabled={!!busy} onClick={() => run("sync", async () => { const r = await sync(); toast.message(`${r.count} accounts found`); })}>Sync from GoHighLevel</Button>
            </div>
            {s?.accounts.length === 0 && <p className="text-sm text-muted-foreground">No accounts yet — click Sync.</p>}
            {s?.accounts.map((a: any) => {
              const video = ["youtube", "tiktok"].includes(a.platform.toLowerCase());
              return (
                <div key={a.id} className="flex items-center justify-between text-sm">
                  <span><b className="capitalize">{a.platform}</b> · {a.name}{video && <span className="text-muted-foreground"> (video only — skipped for image posts)</span>}</span>
                  <Switch checked={a.enabled} onCheckedChange={(v) => run("tog", () => tog({ data: { id: a.id, enabled: v } }))} />
                </div>
              );
            })}
          </Card>
          <TemplateCard templates={s?.templates ?? []} onAdd={(t) => run("tpl", () => saveTpl({ data: t }), "Template added")} />
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <HourlyQueue onChange={refresh} />
        <MediaQueue drafts={s?.drafts ?? []} posts={s?.posts ?? []} onPick={setSelId} />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <CarouselMaker draft={sel as any} />
        <ShortVideoMaker draft={sel as any} />
      </div>
    </div>
  );
}

function Calendar({ posts, drafts, onPick }: { posts: any[]; drafts: any[]; onPick: (id: string) => void }) {
  const days = useMemo(() => {
    const start = new Date(); start.setHours(0, 0, 0, 0);
    return Array.from({ length: 90 }, (_, i) => new Date(start.getTime() + i * 86400_000));
  }, []);
  const byDay = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const p of posts) { const k = dayKey(new Date(p.scheduled_for)); m.set(k, [...(m.get(k) ?? []), p]); }
    return m;
  }, [posts]);
  return (
    <Card className="p-4">
      <h2 className="font-semibold mb-3">Next 90 days</h2>
      <div className="grid grid-cols-7 md:grid-cols-15 gap-1" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(64px, 1fr))" }}>
        {days.map((d) => {
          const list = byDay.get(dayKey(d)) ?? [];
          return (
            <div key={d.toISOString()} className={`rounded border border-border p-1 min-h-14 text-[10px] ${list.length ? "bg-accent" : ""}`}>
              <div className="text-muted-foreground">{d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
              {list.slice(0, 2).map((p) => {
                const dr = drafts.find((x) => x.id === p.draft_id);
                return <button key={p.id} onClick={() => onPick(p.draft_id)} className={`block w-full truncate text-left ${p.status === "failed" ? "text-destructive" : "text-foreground"}`}>{dr?.headline ?? p.status}</button>;
              })}
              {list.length > 2 && <div>+{list.length - 2}</div>}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function DraftEditor({ draft, accounts, busy, onSave, onReject, onSchedule }: {
  draft: any; accounts: any[]; busy: string | null;
  onSave: (p: any) => void; onReject: () => void; onSchedule: (when: string, ids: string[]) => void;
}) {
  const [headline, setHeadline] = useState(draft.headline);
  const [subline, setSubline] = useState(draft.subline ?? "");
  const [caption, setCaption] = useState(draft.caption);
  const [hashtags, setHashtags] = useState((draft.hashtags ?? []).join(" "));
  const [palette, setPalette] = useState<number>(draft.palette);
  const [style, setStyle] = useState<Style>("bold");
  const [cutout, setCutout] = useState(0);
  const [previews, setPreviews] = useState<Partial<Record<SizeKey, string>>>({});
  const [rendering, setRendering] = useState(false);
  const [when, setWhen] = useState(() => { const d = new Date(Date.now() + 3600_000); d.setMinutes(0, 0, 0); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); });
  const enabled = accounts.filter((a) => a.enabled && !["youtube", "tiktok"].includes(a.platform.toLowerCase()));
  const [ids, setIds] = useState<string[]>(enabled.map((a) => a.ghl_account_id));
  const hasImages = draft.image_square && draft.image_vertical && draft.image_landscape;

  useEffect(() => {
    let alive = true; const urls: string[] = [];
    (async () => {
      const out: Partial<Record<SizeKey, string>> = {};
      for (const k of Object.keys(SIZES) as SizeKey[]) {
        const b = await renderGraphic({ headline, subline, format: draft.format, palette, style, cutout }, k);
        const u = URL.createObjectURL(b); urls.push(u); out[k] = u;
      }
      if (alive) setPreviews(out);
    })();
    return () => { alive = false; urls.forEach(URL.revokeObjectURL); };
  }, [headline, subline, palette, draft.format, style, cutout]);

  async function renderAndSave() {
    setRendering(true);
    try {
      const patch: Record<string, string> = {};
      for (const k of Object.keys(SIZES) as SizeKey[]) {
        const b = await renderGraphic({ headline, subline, format: draft.format, palette, style, cutout }, k);
        const path = `drafts/${draft.id}/${k}.png`;
        const { error } = await supabase.storage.from("social-media").upload(path, b, { upsert: true, contentType: "image/png" });
        if (error) throw error;
        patch[`image_${k}`] = path;
      }
      onSave({ headline, subline, caption, hashtags: hashtags.split(/\s+/).filter(Boolean), palette, ...patch });
    } catch (e) { toast.error((e as Error).message); } finally { setRendering(false); }
  }

  return (
    <Card className="p-5 space-y-4">
      <div className="grid gap-3 md:grid-cols-3">
        {(Object.keys(SIZES) as SizeKey[]).map((k) => (
          <figure key={k} className="space-y-1">
            {previews[k] ? <img src={previews[k]} alt={SIZES[k].label} className="w-full rounded border border-border" /> : <div className="aspect-square bg-muted rounded" />}
            <figcaption className="text-xs text-muted-foreground">{SIZES[k].label}</figcaption>
          </figure>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {STYLES.map((s) => (
          <Button key={s.id} size="sm" variant={style === s.id ? "default" : "outline"} onClick={() => setStyle(s.id)}>{s.label}</Button>
        ))}
      </div>
      {style !== "classic" && style !== "card" && (
        <div className="flex flex-wrap gap-2">
          {CUTOUTS.map((u, i) => (
            <button key={u} type="button" onClick={() => setCutout(i)} aria-label={`Photo ${i + 1}`}
              className={`h-14 w-14 overflow-hidden rounded-full border-2 bg-muted ${cutout === i ? "border-foreground" : "border-border"}`}>
              <img src={u} alt="" className="h-full w-full object-cover object-top" loading="lazy" />
            </button>
          ))}
        </div>
      )}
      {style === "classic" && <div className="flex flex-wrap gap-2">
        {PALETTES.map((p, i) => (
          <Button key={p.name} size="sm" variant={palette === i ? "default" : "outline"} onClick={() => setPalette(i)}>{p.name}</Button>
        ))}
      </div>}
      <Input value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="Headline" />
      <Input value={subline} onChange={(e) => setSubline(e.target.value)} placeholder="Subline" />
      <Textarea rows={7} value={caption} onChange={(e) => setCaption(e.target.value)} />
      <Input value={hashtags} onChange={(e) => setHashtags(e.target.value)} />
      <div className="flex flex-wrap gap-2">
        <Button onClick={renderAndSave} disabled={rendering || !!busy}>{rendering ? "Rendering…" : "Save & render graphics"}</Button>
        <Button variant="ghost" onClick={onReject} disabled={!!busy}>Reject</Button>
      </div>
      <div className="border-t border-border pt-4 space-y-3">
        <h3 className="font-semibold">Schedule</h3>
        {!hasImages && <p className="text-sm text-muted-foreground">Save & render graphics first.</p>}
        <div className="flex flex-wrap gap-2">
          {enabled.length === 0 && <span className="text-sm text-muted-foreground">No enabled accounts — sync GoHighLevel below.</span>}
          {enabled.map((a) => (
            <Button key={a.id} size="sm" variant={ids.includes(a.ghl_account_id) ? "default" : "outline"}
              onClick={() => setIds((s) => s.includes(a.ghl_account_id) ? s.filter((x) => x !== a.ghl_account_id) : [...s, a.ghl_account_id])}>
              {a.platform} · {a.name}
            </Button>
          ))}
        </div>
        <div className="flex gap-2">
          <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="w-60" />
          <Button disabled={!hasImages || ids.length === 0 || !!busy} onClick={() => onSchedule(new Date(when).toISOString(), ids)}>Add to queue</Button>
        </div>
      </div>
    </Card>
  );
}

function TemplateCard({ templates, onAdd }: { templates: any[]; onAdd: (t: { format: any; topic: string; prompt_hint?: string }) => void }) {
  const [format, setFormat] = useState("tip_card");
  const [topic, setTopic] = useState("");
  const [hint, setHint] = useState("");
  return (
    <Card className="p-4 space-y-3">
      <h2 className="font-semibold">Post templates (the auto-writer rotates through these)</h2>
      <div className="flex gap-2">
        <Select value={format} onValueChange={setFormat}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>{FORMATS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Input placeholder="Topic" value={topic} onChange={(e) => setTopic(e.target.value)} />
      </div>
      <Input placeholder="Extra direction (optional)" value={hint} onChange={(e) => setHint(e.target.value)} />
      <Button size="sm" disabled={topic.length < 3} onClick={() => { onAdd({ format, topic, prompt_hint: hint || undefined }); setTopic(""); setHint(""); }}>Add template</Button>
      <ul className="text-sm space-y-1">{templates.map((t) => <li key={t.id}><Badge variant="outline">{t.format}</Badge> {t.topic}</li>)}</ul>
    </Card>
  );
}
